/* =====================================================================
   SCK: re-inject Wishlist Plus collection hearts after in-page grid updates

   Why this exists
   The Wishlist Plus "Collections" app embed injects one heart per product
   card and watches the collection section with a MutationObserver that is
   debounced (300 ms) and only inspects the LAST batch of DOM changes.
   When a shopper filters, removes a filter tag, clears filters, moves the
   price slider or uses Back/Forward, theme.js re-renders
   #CollectionAjaxContent and then, in a later task, rebuilds the price
   slider (initPriceRange). The slider batch arrives last, contains no
   product cards, and the app never re-injects, so every card on the
   updated grid loses its heart until the page is reloaded.

   What this does
   theme.js dispatches "collection:reloaded" once after every such update.
   On that event this script fetches the shopper's wishlist and asks the
   app embed to inject hearts into the cards currently in the grid. Cards
   that already have a heart are skipped by the app, so no duplicates.

   Overlapping updates: one pass runs at a time. An update that arrives
   during a pass is remembered, and one more pass runs as soon as the
   current one ends, so the newest grid always gets hearts.

   Failures: if the wishlist request fails, the pass ends without
   injecting (as the app itself does on page load) and the next update
   tries again. Every exit path releases the lock. No timers, polling or
   extra observers; the listener is bound once per page.

   Wishlist Plus functions used (internal unless noted):
     window.SwymCallbacks                      documented ready hook
     _swat.api.fetch / _swat.api.fetchLists    Swym JS API (wishlist read)
     _swat.collectionsApi.getDuAndEpiOfWishlistedProducts
     _swat.collectionsApi.findProductCardsAndInjectButton
     _swat.collectionsApi.initializeCollections   fallback only
     window.SwymCollectionsConfig.CollectionsSelectors
   ===================================================================== */
(function () {
  if (window.__sckWishlistReinjectBound) return;
  window.__sckWishlistReinjectBound = true;

  var running = false;
  var pending = false;

  function isFn(f) {
    return typeof f === 'function';
  }

  // Calls done(lists) on success or done(null) on failure, exactly once.
  function fetchSaved(swat, multiList, done) {
    var settled = false;
    var once = function (value) {
      if (settled) return;
      settled = true;
      done(value);
    };
    try {
      var api = swat.api || {};
      if (multiList && isFn(api.fetchLists)) {
        api.fetchLists({
          callbackFn: function (lists) { once(lists || []); },
          errorFn: function () { once(null); }
        });
      } else if (!multiList && isFn(api.fetch)) {
        api.fetch(function (items) { once(items || []); }, function () { once(null); });
      } else {
        once(null);
      }
    } catch (e) {
      once(null);
    }
  }

  function run(swat) {
    var ca = swat && swat.collectionsApi;
    var cfg = window.SwymCollectionsConfig;
    if (!ca || !cfg) return;

    if (running) {
      pending = true;
      return;
    }
    running = true;
    pending = false;

    var finish = function () {
      running = false;
      if (pending) run(swat);
    };

    var multiList = !!(
      swat.retailerSettings &&
      swat.retailerSettings.Wishlist &&
      swat.retailerSettings.Wishlist.EnableCollections
    );
    var api = swat.api || {};
    var canInject =
      isFn(ca.getDuAndEpiOfWishlistedProducts) &&
      isFn(ca.findProductCardsAndInjectButton) &&
      Array.isArray(cfg.CollectionsSelectors) &&
      (multiList ? isFn(api.fetchLists) : isFn(api.fetch));

    if (!canInject) {
      // Fallback if the app's internals change: the entry point the app
      // itself calls on re-render (true = do not add another observer).
      try {
        if (isFn(ca.initializeCollections)) {
          ca.initializeCollections(swat, true, window.Shopify && Shopify.theme && Shopify.theme.schema_name);
        }
      } catch (e) {}
      finish();
      return;
    }

    fetchSaved(swat, multiList, function (lists) {
      if (!lists) {
        finish(); // request failed: inject nothing now, retry on next update
        return;
      }
      try {
        var saved = ca.getDuAndEpiOfWishlistedProducts(lists, multiList, swat);
        Promise.resolve(
          ca.findProductCardsAndInjectButton(saved, cfg.CollectionsSelectors, multiList, swat)
        ).then(finish, finish);
      } catch (e) {
        finish();
      }
    });
  }

  // Bind once Wishlist Plus is ready (a callback added after it is ready
  // runs immediately). Updates before that need nothing: the app's first
  // injection runs against whatever cards are present.
  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function (swat) {
    document.addEventListener('collection:reloaded', function () {
      run(swat);
    });
  });
})();
